import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { useDebouncedCallback } from 'use-debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BidsList from './BidsList';
import EnergyBidsHeader from './energy/BidsHeader';
import SecurityBidsHeader from './security/BidsHeader';
import BidsInputParamConfiguration from './BidsInputParamConfiguration';
import { auctionPropertiesType, auctionType } from '../../types';
import { ALL_BIDS_QUERY_NAME, CANCEL_BIDS, MODIFY_BIDS, ON_BID_CHANGED } from '../../graphql/bids';
import { CLOSED, getUIPhase, RUNNING } from '../../utils/AuctionPhase';
import { hasPrivilege } from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';
import useAuthentication from '../../hooks/useAuthentication';
import './_styles_/BidsPopup.css';
import MutationButton from '../buttons/MutationButton';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import ConfirmButton from '../buttons/ConfirmButton';
import Constants from '../../utils/Constants';
import useExtendedSubscription from '../../hooks/useExtendedSubscription';
import AuctionResult from './AuctionResult';
import useIsMounted from '../../hooks/useIsMounted';
import BidsPlaceBidRow from './BidsPlaceBidRow';
import SubscriptionActions from '../../utils/SubscriptionActions';
import { ENERGY, getProductType, SECURITY } from '../../utils/ProductTypes';
import BidsAuctionConfiguration from './BidsAuctionConfiguration';
import UploadButton from '../buttons/UploadButton';
import { getBiddingType, OPEN } from '../../utils/BiddingTypes';
import { getBiddingUserConfiguration } from '../../reducers';
import DownloadSampleFile from '../DownloadSampleFile';

const DEBOUNCE_TRANSITION_DELAY = 300; // ms

export const handleAction = (onBidAdded, onBidModified, onBidCancelled) => (action) => {
    switch (action) {
        case SubscriptionActions.BIDS_CREATED:
            onBidAdded();
            break;
        case SubscriptionActions.BIDS_UPDATED:
            onBidModified();
            break;
        case SubscriptionActions.BIDS_DELETED:
            onBidCancelled();
            break;
        default:
            // none
    }
};

export const RESET_AFTER_MODIFICATION_SUCCESS_DELAY = 300;

BidsPopup.propTypes = {
    onCancelHandler: PropTypes.func.isRequired,
    auction: auctionType.isRequired,
    auctionProps: auctionPropertiesType.isRequired,
    popupRefetch: PropTypes.func.isRequired,
};

function BidsPopup(props) { // NOSONAR javascript:S3776
    const {
        onCancelHandler,
        auction,
        bids,
        auctionMembers,
        auctionProps,
        popupRefetch,
    } = props;
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const productType = getProductType(auction.product.productType);
    const biddingType = getBiddingType(auction.biddingType);
    const [selectedBidIds, setSelectedBidIds] = useState([]);
    const cancelBidsExtendedMutation = useExtendedMutation(CANCEL_BIDS, {
        variables: { bidIds: selectedBidIds },
        successCallback: () => {
            setSelectedBidIds([]);
            onBidCancelled();
        },
        failureMessageOptions: { key: 'auctions.bids-list.notification.bids-cancelled-failure' },
        refetchQueries: [ALL_BIDS_QUERY_NAME],
    });

    const [bidModifications, setBidModifications] = useState([]);
    const [fullBidModifications, setFullBidModifications] = useState([]);
    const [isModificationInvalid, setModificationInvalid] = useState(true);
    const handleBidModifications = (newBidModifications) => {
        setModificationInvalid(newBidModifications.some((bidModification) => !bidModification.isValid));
        setFullBidModifications(newBidModifications);
        setBidModifications(newBidModifications.map((bidModification) => {
            const newBidModification = { ...bidModification };
            delete newBidModification.isValid;
            return newBidModification;
        }));
    };
    // due to bug in Apollo useQuery (QueryData) we need to wait a bit before calling refetch so
    // the actual obsRefetch() does not call refetch() method on undefined currentObservable
    // (see https://github.com/apollographql/apollo-client/blob/master/src/react/data/QueryData.ts#L464)
    const isMounted = useIsMounted();
    const successModificationCallback = useDebouncedCallback(() => {
        // refetchQueries property of useExtendedMutation() cannot be used, otherwise resetModifications()
        // is processed before the actual refetch and therefore old bid values "blink" after
        // resetModifications() and before actual refetch of new loaded bid values
        if (isMounted) {
            popupRefetch({ auctionId: auction.id }).then(resetModifications);
            onBidModified();
        }
    }, RESET_AFTER_MODIFICATION_SUCCESS_DELAY);
    const modifyBidsExtendedMutation = useExtendedMutation(MODIFY_BIDS, {
        variables: { bidModifications },
        successCallback: successModificationCallback,
        failureMessageOptions: { key: 'auctions.bids-list.notification.bid-modification-failure', params: { count: bidModifications.length } },
    });
    const resetModifications = () => {
        setFullBidModifications([]);
        setBidModifications([]);
    };

    const [isInTransition, setInTransitionBase] = useState(false);
    const [transitionClass, setTransitionClassBase] = useState('');
    const setInTransition = useDebouncedCallback(setInTransitionBase, DEBOUNCE_TRANSITION_DELAY);
    const setTransitionClass = useDebouncedCallback(setTransitionClassBase, DEBOUNCE_TRANSITION_DELAY);
    const onBidAdded = () => {
        setTransitionClass('bid-added');
        setInTransition(true);
    };
    const onBidCancelled = () => {
        setTransitionClass('bid-cancelled');
        setInTransition(true);
    };
    const onBidModified = () => {
        setTransitionClass('bid-modified');
        setInTransition(true);
    };
    const handleTransitionEntered = () => {
        setInTransitionBase(false);
    };
    const handleTransitionExited = () => {
        setTransitionClassBase('');
    };
    useExtendedSubscription(ON_BID_CHANGED, {
        refetch: popupRefetch,
        variables: { auctionId: auction.id },
        refetchVariables: { auctionId: auction.id },
        dataProperty: 'bidChanged',
        onChangeCallback: ({ action, model: { models } }) => {
            handleAction(onBidAdded, onBidModified, onBidCancelled)(action);
            if (action === SubscriptionActions.BIDS_UPDATED || action === SubscriptionActions.BIDS_DELETED) {
                const changedBidIds = models.map((bidModel) => bidModel.id);
                const selectedBidIdsAfterBidChange = selectedBidIds.filter((selectedBidId) => changedBidIds.indexOf(selectedBidId) < 0);
                setSelectedBidIds(selectedBidIdsAfterBidChange);
                const bidModificationsAfterBidChange = bidModifications.filter((bidModification) => changedBidIds.indexOf(bidModification.id) < 0);
                setBidModifications(bidModificationsAfterBidChange);
                const fullBidModificationsAfterBidChange = fullBidModifications.filter((fullBidModification) => changedBidIds.indexOf(fullBidModification.id) < 0);
                setFullBidModifications(fullBidModificationsAfterBidChange);
            }
        },
    });

    const uiPhase = getUIPhase(auction.phase);
    const isEditable = (uiPhase.is(RUNNING) && hasPrivilege(userPrivileges, [Privileges.BID_EDIT_OWN, Privileges.BID_EDIT_ALL, Privileges.BID_EDIT_AS_BROKER]))
        || (uiPhase.is(CLOSED) && hasPrivilege(userPrivileges, [Privileges.BID_EDIT_ALL]));
    const biddingUserConfiguration = useSelector(getBiddingUserConfiguration);
    let isConflictWithBiddingUserConfigurationQuantityStep = false;
    let isConflictWithBiddingUserConfigurationPriceStep = false;
    let initialQuantityStep = auction.smallestUnit;
    if (biddingUserConfiguration.quantityStep !== null && biddingType.is(OPEN)) {
        if (biddingUserConfiguration.quantityStep >= auction.smallestUnit
            && biddingUserConfiguration.quantityStep <= (auction.maxBidSize || auction.quantity)
            && (biddingUserConfiguration.quantityStep % auction.smallestUnit) === 0) {
            initialQuantityStep = biddingUserConfiguration.quantityStep;
        } else {
            isConflictWithBiddingUserConfigurationQuantityStep = true;
        }
    }
    const [quantityStep, setQuantityStep] = useState(initialQuantityStep);
    let initialPriceStep = Constants.MIN_PRICE_STEP;
    if (biddingUserConfiguration.priceStep !== null) {
        if (biddingUserConfiguration.priceStep >= Constants.MIN_PRICE_STEP
            && (!auction.maxPrice || biddingUserConfiguration.priceStep <= auction.maxPrice)) {
            initialPriceStep = biddingUserConfiguration.priceStep;
        } else {
            isConflictWithBiddingUserConfigurationPriceStep = true;
        }
    }
    const [priceStep, setPriceStep] = useState(initialPriceStep);
    const [isQuantityStepValid, setQuantityStepValid] = useState(true);
    const [isPriceStepValid, setPriceStepValid] = useState(true);

    const onQuantityStepValidated = useCallback((newValue, isValid) => {
        setQuantityStepValid(isValid);
    }, []);
    const onPriceStepValidated = useCallback((newValue, isValid) => {
        setPriceStepValid(isValid);
    }, []);

    return (
        <div className={`auction-bids ${uiPhase.id}`}>
            {productType.is(ENERGY) && (
                <EnergyBidsHeader hideHandler={onCancelHandler} auction={auction} />
            )}
            {productType.is(SECURITY) && (
                <SecurityBidsHeader hideHandler={onCancelHandler} auction={auction} />
            )}
            {isEditable && (
                <div className="auction-bids--place-bid--container">
                    <BidsPlaceBidRow
                        auction={auction}
                        auctionMembers={auctionMembers.filter((auctionMember) => !auctionMember.suspended)}
                        bids={bids}
                        isQuantityStepValid={isQuantityStepValid}
                        quantityStep={quantityStep}
                        isPriceStepValid={isPriceStepValid}
                        priceStep={priceStep}
                        onBidAdded={onBidAdded}
                    />
                    <BidsInputParamConfiguration
                        auction={auction}
                        onQuantityStepChange={setQuantityStep}
                        onPriceStepChange={setPriceStep}
                        onQuantityStepValidation={onQuantityStepValidated}
                        onPriceStepValidation={onPriceStepValidated}
                        quantityStep={quantityStep}
                        priceStep={priceStep}
                        isQuantityStepValid={isQuantityStepValid}
                        isPriceStepValid={isPriceStepValid}
                        isConflictWithBiddingUserConfigurationPriceStep={isConflictWithBiddingUserConfigurationPriceStep}
                        isConflictWithBiddingUserConfigurationQuantityStep={isConflictWithBiddingUserConfigurationQuantityStep}
                    />
                </div>
            )}
            <BidsAuctionConfiguration
                auction={auction}
                auctionProps={auctionProps}
            />
            <AuctionResult auction={auction} />
            <CSSTransition in={isInTransition} timeout={1000} classNames={transitionClass} onEntered={handleTransitionEntered} onExited={handleTransitionExited}>
                <BidsList
                    bids={bids}
                    auctionMembers={auctionMembers}
                    auction={auction}
                    uiPhase={uiPhase}
                    isEditable={isEditable}
                    isQuantityStepValid={isQuantityStepValid}
                    quantityStep={quantityStep}
                    isPriceStepValid={isPriceStepValid}
                    priceStep={priceStep}
                    selectedBidIds={selectedBidIds}
                    onBidsSelected={setSelectedBidIds}
                    bidModifications={fullBidModifications}
                    onBidModifications={handleBidModifications}
                />
            </CSSTransition>
            <div className="auction-bids--bottom-buttons">
                <div className="auction-bids--bottom-buttons--left">
                    {isEditable && (
                        <MutationButton
                            className="button button-bordered-red cancel-selected-bids-button"
                            confirmationMessage={t('auctions.bids-list.confirm-cancel-bids', { count: selectedBidIds.length })}
                            extendedMutation={cancelBidsExtendedMutation}
                            disabled={!selectedBidIds.length}
                            showConfirmationDialog
                        >
                            <FontAwesomeIcon icon="times" />&nbsp;{t('auctions.bids-list.button.cancel-bids')}
                        </MutationButton>
                    )}
                    {getBiddingType(auction.biddingType).is(OPEN) && isEditable && (
                        <UploadButton
                            label={t('auctions.bids-list.upload-bids-button')}
                            className="button-bordered upload-bids-button"
                            uploadPath={Constants.UPLOAD_BIDS_PATH}
                            accept=".xls,.xlsx,.csv"
                            onUpload={popupRefetch}
                            extraConfirmationMessage={t('auctions.bids-list.upload-bids-extra-confirmation')}
                            baseVariables={[{
                                name: 'auctionId',
                                value: auction.id,
                            }]}
                        >
                            <DownloadSampleFile
                                text={t('auctions.bids-list.download-sample-file-text')}
                                fileName="sampleBidUploadFile"
                                sampleType="BID"
                                fileTypes={['CSV', 'XLSX']}
                            />
                        </UploadButton>
                    )}
                </div>
                <div className="auction-bids--bottom-buttons--right">
                    {isEditable && (
                        <>
                            <ConfirmButton
                                className="button button-bordered reset-modifications-button"
                                onConfirm={resetModifications}
                                message={t('auctions.bids-list.confirm-reset-modifications')}
                                disabled={!fullBidModifications.length}
                            >
                                <FontAwesomeIcon icon="undo" />&nbsp;{t('auctions.bids-list.button.reset-modifications')}
                            </ConfirmButton>
                            <MutationButton
                                className="button button-filled modify-bids-button"
                                confirmationMessage={t('auctions.bids-list.confirm-modify-bids', { count: fullBidModifications.length })}
                                extendedMutation={modifyBidsExtendedMutation}
                                disabled={!fullBidModifications.length || isModificationInvalid}
                                showConfirmationDialog
                            >
                                <FontAwesomeIcon icon="check" />&nbsp;{t('auctions.bids-list.button.modify-bids')}
                            </MutationButton>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default BidsPopup;
